/**
 * Extension slots in the finding drawer, owned by other tracks:
 * - Resolve slot (T5, `advanced/actions/**`): Resolve button, proposal diff, approval panel, apply/verify/rollback.
 * - Advisor slot (T10, `advanced/advisor/**`): AI advisor thread for this finding + customer-visible expert notes,
 *   expert review / paid call booking.
 *
 * Contract for T5/T10: export a React component typed `ComponentType<ResolveSlotProps>` / `ComponentType<AdvisorSlotProps>`
 * from your own folder's index (e.g. `advanced/actions/index.ts` -> `export const ResolvePanel`). The integrator then
 * registers it in ONE place: `REGISTERED_SLOTS` below (or passes `slots` to <AdvancedReportView>). Until then the
 * placeholders render: a clearly marked, accessible region that says what will appear there.
 *
 * Rules for slot components: render inside the given region (no portals over the drawer), use brand tokens and
 * i18n, never mutate the finding locally - call `onFindingChanged()` after a server-side change so the report reloads.
 */
import { createContext, useContext, useEffect, useState, type ComponentType } from 'react';
import { useSearchParams } from 'react-router';
import type { AdvancedFinding, AdvancedReport } from '@qm/contracts/audit-remediation';
import { Button, Text } from '@qm/ui';
import { useResource } from '../../../../../lib/resource';
import type { GrowthAuditModuleView } from '../../GrowthAuditModuleCard';
import { useFlags } from '../../../../../lib/flags';
import { ResolvePanel } from '../actions';
import { AdvisorPanel, advisorApi, BookExpertDialog, ExpertFindingNotes, ExpertSignoffBadge, type ExpertView, type SuggestedAction } from '../advisor';
import type { Loc } from './model';
import { useReportText } from './text';

/** Props every finding slot receives. */
export interface FindingSlotProps {
  /** Audit job id (path param of every /v1/audit-remediation/jobs/:jobId route). */
  jobId: string;
  /** The finding as currently shown (status already overlaid). */
  finding: AdvancedFinding;
  /** Report content language chosen on the page (may differ from the portal locale). */
  locale: Loc;
  /** customer_owner | customer_admin | customer_member: may start work / propose. */
  canWrite: boolean;
  /** customer_owner | customer_admin: may dismiss, accept risk and (subject to security settings + step-up) approve. */
  canApprove: boolean;
  /** Ask the report to re-fetch (after an approve/apply/rollback or a new advisor/expert action changed state). */
  onFindingChanged: () => void;
}

/** T5 Resolve button + approval panel. */
export type ResolveSlotProps = FindingSlotProps;

/** T10 advisor panel + expert notes. `subject` lets the advisor greet with the business name / domain. */
export interface AdvisorSlotProps extends FindingSlotProps {
  subject: AdvancedReport['subject'];
}

export interface ReportSlotComponents {
  Resolve: ComponentType<ResolveSlotProps>;
  Advisor: ComponentType<AdvisorSlotProps>;
  /** Report header extras (T10 expert sign-off badge). Optional. */
  Header?: ComponentType<{ jobId: string }>;
}

/** Placeholder until T5 registers its component. */
export function ResolveSlotPlaceholder({ finding }: ResolveSlotProps) {
  return <ReportSlotHint id={`resolve-${finding.id}`} kind="resolve" />;
}
/** Placeholder until T10 registers its component. */
export function AdvisorSlotPlaceholder({ finding }: AdvisorSlotProps) {
  return <ReportSlotHint id={`advisor-${finding.id}`} kind="advisor" />;
}

function ReportSlotHint({ id, kind }: { id: string; kind: 'resolve' | 'advisor' }) {
  const { t } = useReportText();
  return <div className="qm-ar-slot" data-slot={kind} data-placeholder="true" id={`slot-${id}`}><Text tone="muted">{t(`audit-remediation.report.slot.${kind}`)}</Text></div>;
}

// ---------- registered components (integrator) ----------

/** T5 resolve flow in the drawer (flag `audit.remediation`; off: the placeholder stays). */
export function RegisteredResolveSlot(p: ResolveSlotProps) {
  const flag = useFlags();
  const { t } = useReportText();
  const access = useResource<GrowthAuditModuleView>('/v1/growth-audit/module');
  if (flag(SLOT_FLAGS.remediation) !== 'on') return <ResolveSlotPlaceholder {...p} />;
  if (!p.canWrite || access.status !== 'ready' || !access.data.capabilities.resolvePlan) return <div className="qm-ar-slot" data-slot="resolve"><Button disabled>{t('shell.auditResolve.resolve')}</Button><Text>{t('shell.auditResolve.locked')}</Text></div>;
  return <div className="qm-ar-slot" data-slot="resolve" id={`slot-resolve-${p.finding.id}`}><ResolvePanel finding={p.finding} jobId={p.jobId} /></div>;
}

/** Customer-visible expert data for a job, loaded once per job and shared by every drawer opened on the page. */
const expertCache = new Map<string, Promise<ExpertView | null>>();
function useExpertView(jobId: string, enabled: boolean): ExpertView | null {
  const [view, setView] = useState<ExpertView | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    let p = expertCache.get(jobId);
    if (!p) { p = advisorApi().expert(jobId).catch(() => null); expertCache.set(jobId, p); }
    void p.then((v) => { if (live) setView(v); });
    return () => { live = false; };
  }, [jobId, enabled]);
  return enabled ? view : null;
}

/**
 * T10 advisor thread + the expert's customer-visible notes / severity override for this finding, and the expert-call
 * booking dialog (opened from the advisor's "book an expert" action or from the resolve flow's expert link,
 * `?finding=<id>&expert=1`). Flags: `audit.advisor`, `audit.expert`, `audit.expert_booking`.
 */
export function RegisteredAdvisorSlot(p: AdvisorSlotProps) {
  const flag = useFlags();
  const [params] = useSearchParams();
  const advisorOn = flag(SLOT_FLAGS.advisor) === 'on';
  const expertOn = flag(SLOT_FLAGS.expert) === 'on';
  const bookingOn = flag(SLOT_FLAGS.expertBooking) === 'on';
  const [booking, setBooking] = useState(() => params.get('expert') === '1' && params.get('finding') === p.finding.id);
  const expert = useExpertView(p.jobId, expertOn);
  if (!advisorOn && !expertOn && !bookingOn) return <AdvisorSlotPlaceholder {...p} />;
  const title = p.locale === 'ar' && p.finding.title.ar ? p.finding.title.ar : p.finding.title.en;
  const onAction = (a: SuggestedAction) => {
    if (a === 'book_expert') { if (bookingOn) setBooking(true); return; }
    if (typeof document !== 'undefined') document.getElementById(`slot-resolve-${p.finding.id}`)?.scrollIntoView({ block: 'nearest' });
  };
  return (
    <div className="qm-ar-slot" data-slot="advisor" id={`slot-advisor-${p.finding.id}`}>
      {advisorOn ? <AdvisorPanel jobId={p.jobId} findingId={p.finding.id} findingTitle={title} canAsk={p.canWrite} onAction={onAction} headingLevel={3} /> : null}
      {expert ? <ExpertFindingNotes findingId={p.finding.id} notes={expert.notes} overrides={expert.severityOverrides} /> : null}
      {bookingOn && booking ? <BookExpertDialog jobId={p.jobId} findingId={p.finding.id} defaultTopic={title} onClose={() => setBooking(false)} /> : null}
    </div>
  );
}

/** Expert sign-off badge for the report header (flag `audit.expert`). */
export function ReportExpertBadge({ jobId }: { jobId: string }) {
  const flag = useFlags();
  const on = flag(SLOT_FLAGS.expert) === 'on';
  const expert = useExpertView(jobId, on);
  if (!on || !expert) return null;
  return <ExpertSignoffBadge signoff={expert.review.signoff} status={expert.review.status} />;
}

const SLOT_FLAGS = { remediation: 'audit.remediation', advisor: 'audit.advisor', expert: 'audit.expert', expertBooking: 'audit.expert_booking' } as const;

/** Integration point: T5 / T10 components registered by the integrator. */
export const REGISTERED_SLOTS: Partial<ReportSlotComponents> = { Resolve: RegisteredResolveSlot, Advisor: RegisteredAdvisorSlot, Header: ReportExpertBadge };

export const DEFAULT_SLOTS: ReportSlotComponents = { Resolve: ResolveSlotPlaceholder, Advisor: AdvisorSlotPlaceholder, ...REGISTERED_SLOTS };

const SlotCtx = createContext<ReportSlotComponents>(DEFAULT_SLOTS);
export const ReportSlotsProvider = SlotCtx.Provider;
export const useReportSlots = () => useContext(SlotCtx);

/** Rendered by the drawer. */
export function ResolveSlot(p: ResolveSlotProps) { const { Resolve } = useReportSlots(); return <Resolve {...p} />; }
export function AdvisorSlot(p: AdvisorSlotProps) { const { Advisor } = useReportSlots(); return <Advisor {...p} />; }
export function HeaderSlot(p: { jobId: string }) { const { Header } = useReportSlots(); return Header ? <Header {...p} /> : null; }

